import React from "react";
import { SWRConfig } from "swr";

import { APIError, requestJson, toErrorBody } from "../data/api";
import { toReportableRequest } from "../core/api_failure";
import { toRequestUrl } from "../lib/fetch_with_retry";
import * as Sentry from "@sentry/browser";

interface SWRClientProps {
  children?: React.ReactNode;
}

/**
 * A 404 from these reads is the API answering that the thing asked for does not
 * exist — and on an unauthenticated read the thing asked for is routinely taken
 * straight from the address bar. So the miss is usually a verdict on a URL we
 * did not compose: every one seen in production arrived on a link chopped short
 * or rewritten between our email and the click, naming an offering that is
 * alive and unrenamed. There is no fault to diagnose and no fix to ship, so
 * reporting them only buries the reads that fail for a reason we own.
 *
 * Matching on the status is safe here, where matching on it would not be at
 * checkout: a GET answers 404 for exactly one thing, that the addressed
 * resource is absent. Nothing else shares the status, so nothing else is
 * silenced with it — every other failing status still reports.
 *
 * Only the report is skipped. The error is still thrown, so callers and the
 * boundary above them behave exactly as before.
 */
const ABSENT_RESOURCE_STATUS = 404;

// Longest server message still read as a diagnostic rather than a document.
const MAX_DIAGNOSTIC_MESSAGE_LENGTH = 200;

/**
 * How a failing read is titled.
 *
 * The server's own message is kept, because one message per distinct backend
 * fault is exactly the granularity worth having — a missing order and a
 * database running out of memory are different problems and should not share a
 * group.
 *
 * What it must not be is a document. When infrastructure answers in the API's
 * place, the "message" is a whole HTML error page, and titling the report with
 * it names the group after a page — a fresh group for every page, none of them
 * legible, none of them recurring. The status says the same thing in one word,
 * and the body is kept alongside either way.
 */
function toReportedFailure(message: string, status: number): string {
  const isDiagnostic =
    !!message &&
    message.length <= MAX_DIAGNOSTIC_MESSAGE_LENGTH &&
    !message.trimStart().startsWith("<");

  return isDiagnostic ? message : `HTTP ${status}`;
}

/**
 * The default SWR fetcher: every read that is not inside an authenticated
 * subtree goes through it, since the dashboard and the member portal mount
 * their own signed fetchers over the top of this one.
 *
 * Exported for its own tests — what it does with a failing response is
 * otherwise only reachable through a rendered page whose URL names something
 * that is not there.
 */
export async function defaultFetcher(input: RequestInfo) {
  const { status, body } = await requestJson(input);

  if (status >= 200 && status < 300) {
    return body;
  }

  const { message, code } = toErrorBody(body);
  const url = toRequestUrl(input);

  if (status !== ABSENT_RESOURCE_STATUS) {
    Sentry.withScope((scope) => {
      // Redacted, not raw: some of these reads name a signed token, and a
      // report is retained and widely readable.
      scope.setExtras({ input: toReportableRequest(url), status, body });

      Sentry.captureMessage(
        `SWR Error: ${toReportedFailure(message, status)}`,
        "error"
      );
    });
  }

  // The request rides on the error, which is the only way anything downstream
  // learns it: whoever reports this failure — the boundary above, or the hook
  // that announces a failed availability lookup — sees the error and nothing
  // of the read that produced it.
  const error = new APIError(message, code, status);
  error.url = url;

  throw error;
}

export function SWRClient(props: SWRClientProps) {
  const { children } = props;

  return (
    <SWRConfig
      value={{
        fetcher: defaultFetcher,
      }}
    >
      {children}
    </SWRConfig>
  );
}
